const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const NUMERO = new Intl.NumberFormat('pt-BR')

export function formatarMoeda(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '-'
  return MOEDA.format(valor)
}

export function formatarNumero(valor: number): string {
  return NUMERO.format(valor)
}

// O plural mora no pacote compartilhado, para a tela e o servidor escreverem igual.
export { plural } from '@crm/shared'

export function formatarData(iso: string | null | undefined): string {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('pt-BR')
}

/**
 * Data pura ("2026-09-30"), sem hora, como a de início e fim de meta.
 *
 * `formatarData` passaria por `new Date`, que lê a data pura como meia-noite em
 * UTC; no horário de Brasília isso é 21h do dia anterior, e o fim de setembro
 * aparecia como 29/09.
 */
export function formatarDia(dia: string | null | undefined): string {
  if (!dia) return '-'
  const [ano, mes, data] = dia.slice(0, 10).split('-')
  return `${data}/${mes}/${ano}`
}

/** A mesma data pura como meia-noite local, para contas de calendário. */
export function diaLocal(dia: string): Date {
  const [ano, mes, data] = dia.slice(0, 10).split('-').map(Number)
  return new Date(ano, mes - 1, data)
}

/**
 * Data e hora até os minutos: "01/10/2026, 10:00".
 *
 * Os segundos saíram a pedido: em mensagem, agendamento e log ninguém lê
 * "10:00:00", e o ":00" a mais só alonga a linha.
 */
const DATA_HORA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '-'
  return DATA_HORA.format(new Date(iso))
}

/**
 * O valor do campo `datetime-local` ("2026-10-18T15:30") para um instante, na hora de quem olha.
 *
 * `iso.slice(0, 16)` dá a hora em UTC: no horário de Brasília, o retorno marcado para 15h reabria o
 * campo às 18h, e salvar sem mexer adiava o retorno em três horas.
 */
export function paraCampoDataHora(iso: string | Date): string {
  const data = new Date(iso)
  return new Date(data.getTime() - data.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

/** Aceita "1.500,00" e "1500.00" e devolve número, ou null quando não dá. */
export function lerMoeda(texto: string): number | null {
  const limpo = texto.trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.')
  if (!limpo) return null
  const numero = Number(limpo)
  return Number.isFinite(numero) ? numero : null
}

const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' })
const DIA_DA_SEMANA = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' })
const DIA_E_MES = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' })

/** "14:32". */
export function formatarHora(data: Date): string {
  return HORA.format(data)
}

/** O dia 1 do mês da data, à meia-noite local. */
export function inicioDoMes(data: Date): Date {
  return new Date(data.getFullYear(), data.getMonth(), 1)
}

/** "outubro de 2026" vira "Outubro de 2026". */
export function comMaiusculaInicial(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

/**
 * O carimbo de hora da lista de conversas, no formato do WhatsApp.
 *
 * "14:32" hoje, "Ontem", o dia da semana dentro da semana e "12/08" mais
 * atrás. O que interessa muda com a distância: de manhã se pergunta a que
 * horas o cliente escreveu; de um mês atrás, só o dia.
 */
export function horarioDaLista(iso: string | null | undefined): string {
  if (!iso) return ''
  const quando = new Date(iso)
  if (Number.isNaN(quando.getTime())) return ''

  // Compara por dia do calendário, não por 24 horas corridas: às 00h10 uma
  // mensagem das 23h de ontem tem duas horas de idade e mesmo assim é "Ontem".
  const meiaNoite = new Date()
  meiaNoite.setHours(0, 0, 0, 0)
  const diasAtras = Math.floor((meiaNoite.getTime() - quando.getTime()) / 86_400_000) + 1

  if (diasAtras <= 0) return HORA.format(quando)
  if (diasAtras === 1) return 'Ontem'
  if (diasAtras < 7) return DIA_DA_SEMANA.format(quando).replace('.', '')
  return DIA_E_MES.format(quando)
}
